(function () {
    interface ValidatorMetaData {
        model?: {
            message?: string;
            maxLength?: number | string;
            maxlength?: number | string;
            max?: number | string;
            validationValue?: number | string;
            valueToCompare?: number | string;
            value?: number | string;
        };
        maxLength?: number | string;
        maxlength?: number | string;
        max?: number | string;
        validationValue?: number | string;
        valueToCompare?: number | string;
    }

    interface ValidationResult {
        isValid: boolean;
        message: string;
    }

    type ValidateFunction = (
        fieldName: string,
        fieldValue: string,
        validatorMetaData: ValidatorMetaData | null
    ) => ValidationResult | true;

    interface ValidatorObject {
        validate: ValidateFunction;
    }

    interface EpiWindow extends Window {
        epi?: {
            EPiServer?: {
                Forms?: {
                    Validators?: Record<string, ValidatorObject | ValidateFunction>;
                };
            };
        };
    }

    const epiWindow = window as EpiWindow;
    epiWindow.epi = epiWindow.epi || {};
    epiWindow.epi.EPiServer = epiWindow.epi.EPiServer || {};
    epiWindow.epi.EPiServer.Forms = epiWindow.epi.EPiServer.Forms || {};
    epiWindow.epi.EPiServer.Forms.Validators = epiWindow.epi.EPiServer.Forms.Validators || {};

    const validators = epiWindow.epi.EPiServer.Forms.Validators;

    const keyPascal = "Securitas.PublicWeb.Web.Models.Elements.MaxLengthValidator";
    const keyLower = keyPascal.toLowerCase();

    const builtInKeyPascal = "EPiServer.Forms.Implementation.Validation.MaxLengthValidator";
    const builtInKeyLower = builtInKeyPascal.toLowerCase();

    function parsePositiveInt(value: unknown): number | null {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === "number") {
            return Number.isInteger(value) && value > 0 ? value : null;
        }

        if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed.length === 0) {
                return null;
            }

            const parsed = Number(trimmed);
            return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
        }

        return null;
    }

    function getInput(fieldName: string): HTMLInputElement | HTMLTextAreaElement | null {
        try {
            const esc = CSS.escape(fieldName);
            return document.querySelector(`input[name="${esc}"], textarea[name="${esc}"]`);
        } catch {
            return document.querySelector(`input[name="${fieldName}"], textarea[name="${fieldName}"]`);
        }
    }

    function normalizeMaxLengthAttributes(): void {
        const elements = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input[maxlength], textarea[maxlength]");

        elements.forEach((element) => {
            const maxLengthAttr = element.getAttribute("maxlength");
            if (!maxLengthAttr) {
                return;
            }

            if (!element.hasAttribute("data-max-length")) {
                element.setAttribute("data-max-length", maxLengthAttr);
            }

            element.removeAttribute("maxlength");
        });
    }

    function getMaxLengthFromMeta(meta: ValidatorMetaData | null): number | null {
        if (!meta || typeof meta !== "object") {
            return null;
        }

        const direct = parsePositiveInt(meta.maxLength) ??
            parsePositiveInt(meta.maxlength) ??
            parsePositiveInt(meta.max) ??
            parsePositiveInt(meta.validationValue) ??
            parsePositiveInt(meta.valueToCompare);

        if (direct) {
            return direct;
        }

        if (meta.model && typeof meta.model === "object") {
            const modelValue = parsePositiveInt(meta.model.maxLength) ??
                parsePositiveInt(meta.model.maxlength) ??
                parsePositiveInt(meta.model.max) ??
                parsePositiveInt(meta.model.validationValue) ??
                parsePositiveInt(meta.model.valueToCompare) ??
                parsePositiveInt(meta.model.value);

            if (modelValue) {
                return modelValue;
            }
        }

        return null;
    }

    function getMaxLengthFromDom(input: HTMLInputElement | HTMLTextAreaElement | null): number | null {
        if (!input) {
            return null;
        }

        return parsePositiveInt(input.getAttribute("maxlength")) ??
            parsePositiveInt(input.dataset.maxLength) ??
            parsePositiveInt(input.dataset.maxlength) ??
            parsePositiveInt(input.getAttribute("data-max-length"));
    }

    function coreValidate(fieldName: string, fieldValue: string, validatorMetaData: ValidatorMetaData | null): ValidationResult | true {
        if (fieldValue === null || fieldValue === undefined || fieldValue.length === 0) {
            return true;
        }

        const input = getInput(fieldName);
        const maxLength = getMaxLengthFromMeta(validatorMetaData) ?? getMaxLengthFromDom(input);

        if (!maxLength || fieldValue.length <= maxLength) {
            return true;
        }

        const template = validatorMetaData?.model?.message || "Maximum allowed length is {0} characters.";
        const message = template.includes("{0}")
            ? template.replace("{0}", String(maxLength))
            : `${template} (${maxLength})`;

        return {
            isValid: false,
            message
        };
    }

    const builtInValidator = validators[builtInKeyPascal] ?? validators[builtInKeyLower];
    const validatorObject: ValidatorObject = builtInValidator && typeof builtInValidator === "object" && "validate" in builtInValidator
        ? builtInValidator
        : { validate: coreValidate };

    validators[keyLower] = validatorObject;
    validators[keyPascal] = validatorObject;
    validators[keyLower + ":fn"] = coreValidate;
    validators[keyPascal + ":fn"] = coreValidate;

    validators[builtInKeyLower] = validatorObject;
    validators[builtInKeyPascal] = validatorObject;
    validators[builtInKeyLower + ":fn"] = coreValidate;
    validators[builtInKeyPascal + ":fn"] = coreValidate;

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", normalizeMaxLengthAttributes);
    } else {
        normalizeMaxLengthAttributes();
    }
})();
